import { safeLocalStorage } from '@/js/lib/localStorage'
import { apiClient } from '@/js/store/services/ApiClient'
import * as _ from 'lodash'
import Vue from 'vue'
import VueRouter from 'vue-router'
import PopupEditor from '@/js/modules/popup/editor/EditorIndex.vue'
import PopupSlideEngine from '@/js/modules/popup/layouts/SlideEngine.vue'
import PopupSlidePublish from '@/js/modules/popup/layouts/PublishLayout.vue'
import PopupSlideSettings from '@/js/modules/popup/layouts/PopupSettings.vue'
import PopupChooseATemplate from '@/js/modules/popup/marketplace/PopupChooseATemplate.vue'
import Branding from '../components/admin/Branding.vue'
import Configurations from '../components/admin/Configurations.vue'
import AdminDashboard from '../components/admin/Dashboard.vue'
import Documentation from '../components/admin/Documentation.vue'
import ReleaseNotes from '../components/admin/ReleaseNotes.vue'
import Templates from '../components/admin/Templates.vue'
import Editor from '../components/Editor.vue'
import LaunchStep from '../components/editor/steps/Launch.vue'
import Maintenance from '../components/Maintenance.vue'
import ChooseATemplate from '../components/marketplace/ChooseATemplate.vue'
import ChooseATemplatePreview from '../components/marketplace/ChooseATemplatePreview.vue'
import Playground from '../components/playground/Playground.vue'
import StripeConnect from '../components/StripeConnect.vue'
import PageEngineV3 from '../components/v3/PageEngine.vue'
import EnterpriseAdminRouteView from '../modules/enterprise/AdminDashboardManager.vue'
import EnterpriseBrandConfiguration from '../modules/enterprise/brandConfiguration/BrandIndex.vue'
import EnterpriseMoreInfo from '../modules/enterprise/CollectMoreInfo/CollectMoreInfo.vue'
import EnterpriseSignup from '../modules/enterprise/signup/indexSignup.vue'
import { editSitePage, launchStep } from '../store/modules/steps'

import EnterpriseIntegrateBuilder from '../modules/enterprise/IntegrateBuilder/IntegrateBuilder.vue';
import EnterpriseIBSetCustomHost from '../modules/enterprise/IntegrateBuilder/SetCustomHost/SetCustomHost.vue';
import EnterpriseIBDnsAccess from '../modules/enterprise/IntegrateBuilder/DnsAccess/DnsAccess.vue';
import EnterpriseIBInviteIT from '../modules/enterprise/IntegrateBuilder/InviteIT/InviteIT.vue';
import EnterpriseIBConfigDns from '../modules/enterprise/IntegrateBuilder/ConfigDns/ConfigDns.vue';
import EnterpriseIBVerifyCustomHostnames from '../modules/enterprise/IntegrateBuilder/VerifyCustomHostnames/VerifyCustomHostnames.vue';

import * as actions from '../store/modules/actions'
import * as getters from '../store/modules/getters'
import * as mutations from '../store/modules/mutations'

Vue.use(VueRouter)

// const popupRouteGuard = (config, next) => {
//   if (!config.STANDALONE_POPUP) {
//     // if STANDALONE_POPUP key is FALSE, then OrgID is not allowed to access STANDALONE_POPUP
//     // so we move then to Editor Choose a Template
//     next({ name: 'choose:a:template' })
//   }
// }

// In Vuejs new router version, 'Navigation-Errors' are classified as errors instead of warnings.
// This code block only ignore such Navigation redirection errors.

const originalPush = VueRouter.prototype.push

VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }

  return originalPush.call(this, location).catch((error) => {
    if (VueRouter.isNavigationFailure(error)) {
      console.debug(
        '%c Navigation Error From Convrrt: ' + error,
        'color: white; background: black;',
      )
      console.debug('Navigation Error From Convrrt: ', error)
      return error
    }
    return Promise.reject(error)
  })
}

export default function Router(store) {
  const routes = [
    {
      path: '/',
      name: 'campaign',
      redirect: '/choose-a-template',
    },
    {
      path: '/playground',
      name: 'playground',
      component: Playground,
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: Maintenance,
    },
    {
      path: '/campaign-redirect',
      beforeEnter(to, from, next) {
        if (['editor:setup', 'choose:a:template'].includes(from.name)) {
          return next(false)
        }

        const query = _.clone(to.query)

        // construct redirectURL for campaign builder
        if (query.redirectUrl) {
          const newUrl = new URL(query.redirectUrl)

          query.redirectUrl = `${newUrl.protocol}//${newUrl.host}${newUrl.pathname}?funnelId=${to.query.funnelId}`
          console.log(newUrl, 'query')
          if (query.redirectUrl !== '' || query.redirectUrl !== '/') {
            safeLocalStorage.setItem('SITE_REDIRECT_URL', query.redirectUrl)
          }
        }

        const siteId = query.siteId
        delete query.siteId

        store.dispatch(
          'loader/start',
          siteId
            ? 'loader.redirecting.to.builder.message'
            : 'loader.redirecting.to.marketplace.message',
        )

        next({
          name: siteId ? 'editor:setup' : 'choose:a:template',
          query,
          params: {
            siteId,
          },
        })
      },
    },
    {
      path: '/editor',
      redirect: '/choose-a-template',
    },
    {
      name: 'editor',
      path: '/editor/:siteId',
      component: Editor,
      meta: {
        editor: true,
      },
      beforeEnter(to, from, next) {
        store.dispatch('loader/start')
        store
          .dispatch(actions.SITE_FETCH_DATA, to.params.siteId)
          .then((_) => {
            console.debug('loading site data', { to, from })
            if (to.name === 'editor') {
              return next({
                name: 'editor:setup',
                query: to.query,
                params: to.params,
              })
            }

            next()
          })
          .catch((err) => {
            console.error(err)
            next(false)
          })
      },
      children: [
        {
          path: 'pages',
          redirect: { name: 'editor' },
        },
        {
          name: 'editor:setup',
          path: 'setup',
          redirect: {
            name: 'editor:design',
          },
        },
        {
          name: 'editor:design',
          path: 'design',
          beforeEnter(to, from, next) {
            const { id } = store.getters[getters.SITE_GET_INDEX]
            next({
              name: 'editor:design:page',
              params: {
                ...to.params,
                pageId: id,
              },
            })
          },
        },
        {
          name: 'editor:design:page',
          path: 'design/:pageId',
          alias: 'design',
          component: () => {
            const config = store.getters[getters.AUTH_GET_USER_CONFIG]
            const isGridBuilder =  store.getters[getters.SITE_HAS_GRIDBUILDER]

            if (config.GRID_BUILDER_V3_DEFAULT_ROUTE && isGridBuilder) {
              return import('../components/v3/PageEngine.vue')
            } else {
              return import('../components/PageEngine')
            }
          },
          beforeEnter(to, from, next) {
            store.dispatch('loader/start', 'editor.loading.message')
            store.commit(mutations.STEPS_SET_STEP, editSitePage.id)
            next()
          },
          props(route) {
            const { pageId } = route.params

            const page = _.chain(store)
              .get('state.site.site.pages')
              .find({ id: pageId })
              .value()

            return {
              page,
              '.path': `${page.firebaseRef}/vdom`,
              popupPath: `${page.firebaseRef}/popup`,
            }
          },
          meta: {
            pageEngine: true,
          },
        },
        // for test v3 builder
        {
          name: 'editor:design:page',
          path: 'design/:pageId/v3',
          alias: 'design',
          component: PageEngineV3,
          beforeEnter(to, from, next) {
            store.dispatch('loader/start', 'editor.loading.message')
            store.commit(mutations.STEPS_SET_STEP, editSitePage.id)
            next()
          },
          props(route) {
            const { pageId } = route.params

            const page = _.chain(store)
              .get('state.site.site.pages')
              .find({ id: pageId })
              .value()

            return {
              page,
              '.path': `${page.firebaseRef}/vdom`,
              popupPath: `${page.firebaseRef}/popup`,
            }
          },
          meta: {
            pageEngine: true,
          },
        },
        {
          name: 'editor:launch',
          path: 'launch',
          component: LaunchStep,
          beforeEnter(to, from, next) {
            store.dispatch('loader/start', 'editor.loading.launch.step.message')
            store.commit(mutations.STEPS_SET_STEP, launchStep.id)
            next()
          },
        },
      ],
    },
    {
      name: 'choose:a:template',
      path: '/choose-a-template',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: ChooseATemplate,
    },
    {
      name: 'choose:a:template:preview',
      path: '/choose-a-template/preview/:templateID',
      alias: '/choose-a-template/:templateID/preview',
      props: true,
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: ChooseATemplatePreview,
    },
    {
      name: 'AdminDashboard',
      path: '/admin/dashboard',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: AdminDashboard,
    },
    {
      name: 'Templates',
      path: '/admin/templates',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: Templates,
    },
    {
      name: 'Branding',
      path: '/admin/branding',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: Branding,
    },
    {
      name: 'Configurations',
      path: '/admin/configurations',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: Configurations,
    },
    {
      name: 'Documentation',
      path: '/admin/documentation',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: Documentation,
    },
    {
      name: 'ReleaseNotes',
      path: '/admin/release-notes',
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop')
        next()
      },
      component: ReleaseNotes,
    },
    {
      name: 'StripeConnect',
      path: '/stripe/connect',
      component: StripeConnect,
    },
    // POPUP STANDALONE ROUTES
    {
      path: '/popup',
      redirect: '/popup/choose-a-template',
    },
    {
      name: 'popup:choose:a:template',
      path: '/popup/choose-a-template',
      beforeEnter(to, from, next) {
        store.dispatch('loader/start', 'editor.loading.message')
        // const config = store.state.config.config
        // popupRouteGuard(config, next)
        store.dispatch('loader/stop')
        next()
      },
      component: PopupChooseATemplate,
    },
    {
      name: 'popup:editor',
      path: '/popup/editor/:popupId',
      component: PopupEditor,
      meta: {
        editor: true,
      },
      beforeEnter(to, from, next) {
        store.dispatch('loader/start')
        // const config = store.state.config.config
        // popupRouteGuard(config, next)
        store
          .dispatch(actions.POPUP_FETCH_DATA, to.params.popupId)
          .then((_) => {
            next()
          })
      },
      children: [
        {
          name: 'popup:editor:design',
          path: 'design',
          component: PopupSlideEngine,
          beforeEnter(to, from, next) {
            store.dispatch('loader/start', 'editor.loading.message')
            store.commit(mutations.STEPS_SET_STEP, editSitePage.id)
            const popup = store.getters[getters.GET_POPUP]

            store.dispatch(actions.ACTIVATE_STANDALONE_BUILDER, true)
            store
              .dispatch(
                actions.SDA_POPUP_FIREBASE_INIT,
                `${popup.slide.firebaseRef}/vdom`,
              )
              .then(() => {
                next()
              })
          },
          props() {
            // TODO: save popup to state.popup.popup
            const popup = store.getters[getters.GET_POPUP]

            return {
              '.path': store.getters[getters.SDA_POPUP_FIREBASE_PATH],
              popup: popup.slide,
              rootPath: store.getters[getters.SDA_POPUP_ROOTPATH],
            }
          },
          meta: {
            pageEngine: true,
          },
        },
        {
          name: 'popup:editor:publish',
          path: 'publish',
          component: PopupSlidePublish,
          props() {
            const popup = store.getters[getters.GET_POPUP]
            return {
              popup: popup.slide,
            }
          },
        },
        {
          name: 'popup:editor:settings',
          path: 'settings',
          component: PopupSlideSettings,
          props() {
            const popup = store.getters[getters.GET_POPUP]
            return {
              popup: popup.slide,
            }
          },
        },
      ],
    },
    {
      name: 'enterprise:dashboard:manager',
      path: '/enterprise',
      component: EnterpriseAdminRouteView,
      beforeEnter(to, from, next) {
        store.dispatch('loader/stop');
        next();
      },
      children: [
        {
          name: 'enterprise:sign:up',
          path: 'signup',
          component: EnterpriseSignup,

          beforeEnter(to, from, next) {
            store.dispatch('loader/stop');
            next();
          },
        },
        {
          name: 'enterprise:collect:more:info',
          path: 'information',
          component: EnterpriseMoreInfo,

          beforeEnter(to, from, next) {
            store.dispatch('loader/stop');
            next();
          },
        },
        {
          name: 'enterprise:branding:page',
          path: 'brand-config',
          component: EnterpriseBrandConfiguration,

          beforeEnter(to, from, next) {
            store.dispatch('loader/stop');
            next();
          },
        },
        {
          name: 'enterprise:ib',
          path: 'integrate-builder',
          component: EnterpriseIntegrateBuilder,

          beforeEnter(to, from, next) {
            store.dispatch('loader/stop');
            next();
          },
          children: [
            {
              name: 'enterprise:ib:set:custom:host',
              path: 'set-custom-host',
              component: EnterpriseIBSetCustomHost,
            },
            {
              name: 'enterprise:ib:dns:access',
              path: 'dns-access',
              component: EnterpriseIBDnsAccess,
            },
            {
              name: 'enterprise:ib:config:dns',
              path: 'config-dns',
              component: EnterpriseIBConfigDns,
            },
            {
              name: 'enterprise:ib:verify:custom:hostnames',
              path: 'verify-custom-hostnames',
              component: EnterpriseIBVerifyCustomHostnames,
            },
            {
              name: 'enterprise:ib:invite:it',
              path: 'invite-it',
              component: EnterpriseIBInviteIT,
            },
          ]
        },
      ],
    },
  ]

  const router = new VueRouter({
    mode: 'history',
    routes,
  })

  // watch firebase auth state
  store.dispatch(actions.AUTH_HEARTBEAT)
  store.dispatch(actions.AUTH_FIREBASE_WATCH_AUTH)

  // load current user
  router.beforeEach(async (to, from, next) => {
    const { MAINTENANCE_ENABLED, AUTH_REDIRECT_URL } = await store.dispatch(
      'config/load',
    )

    if (MAINTENANCE_ENABLED && to.name === 'maintenance') {
      return next()
    }

    if (MAINTENANCE_ENABLED) {
      return next('/maintenance')
    }

    if (store.getters[getters.AUTH_GET_USER]) {
      return next()
    }

    if (to.query.name) {
      store.commit(mutations.SITE_TRIGGER_SETTINGS, true)
    }

    store
      .dispatch(actions.AUTH_LOAD_USER)
      .then((_) => {
        next()
      })
      .catch((error) => {
        const { response = {} } = error
        const { data, status } = response
        next(false)

        if (AUTH_REDIRECT_URL) {
          window.location.href = AUTH_REDIRECT_URL
          return
        }

        console.error('failed to load user', {
          error,
          response,
          status,
          data,
        })

        if (to.query.iframeLogin !== 'true') {
          store.commit(
            'loader/setMessage',
            'Failed to load user, no redirect configured',
          )
        }
      })
  })

  // ensure user is authenticated into firebase
  router.beforeEach((to, from, next) => {
    if (to.name === 'maintenance') {
      return next()
    }

    if (store.getters[getters.AUTH_GET_FIREBASE_USER]) {
      return next()
    }

    store
      .dispatch(actions.AUTH_FIREBASE_LOGIN)
      .then((_) => {
        next()
      })
      .catch((err) => {
        console.error(err)
        next(false)
      })
  })

  router.beforeEach((to, from, next) => {
    let changed = false
    if (from.query.redirectUrl && !to.query.redirectUrl) {
      to.query.redirectUrl = from.query.redirectUrl
      changed = true
      if (to.query.redirectUrl !== '' || to.query.redirectUrl !== '/') {
        safeLocalStorage.setItem('SITE_REDIRECT_URL', to.query.redirectUrl)
      }
    }

    if (from.query.headerTitle && !to.query.headerTitle) {
      to.query.headerTitle = from.query.headerTitle
      changed = true
    }

    if (changed) {
      return next({
        path: to.fullPath,
        query: to.query,
      })
    }

    next()
  })

  router.beforeEach((to, from, next) => {
    store.dispatch(actions.INTERCOM_TRACK, {
      event: 'navigated-to',
      context: {
        route: to.name,
        fullPath: to.fullPath,
      },
    })
    next()
  })

  // router.beforeEach((to, from, next) => {
  //   to.matched.some(r => {
  //     if (r.meta.step) {
  //       store.commit(mutations.STEPS_SET_STEP, r.meta.step)
  //     }
  //   })
  //   next()
  // })

  // When 3rd party cookies are disabled the builder cannot authenticate
  // This provides a way for the parent window to inject an authentication token
  // The token is attached to an axios request interceptor
  // TODO: we may want to append a banner to the site explaining that some features may be degraded
  window.addEventListener(
    'message',
    (event) => {
      const url = new URL(window.location)

      // TODO: add config for allowed origins
      // if (event.origin !== 'http://example.com:8080') return

      // event.source is window.opener
      // event.data is "hello there!"
      console.debug('window message', event.data)
      if (url.searchParams.get('sessionActivated') === 'true') {
        console.debug('user is already authenticated')
        return
      }

      if (event.data.command === 'login' && event.data.token) {
        // console.debug('setting session token', event.data.token)

        // reload the window after authentication
        url.searchParams.set('sessionActivated', 'true')
        // inject session token into all API requests
        apiClient.interceptors.request.use((config) => {
          config.headers['Authorization'] = `Bearer ${event.data.token}`
          return config
        })
      } else {
        console.debug('window message missing data', event)
      }
    },
    false,
  )

  return router
}
